import { supabase } from "../supabase";

/** profiles 행을 Firestore users 문서 모양으로 되접는다 (화면 코드 호환) */
function foldProfile(row) {
  return {
    id: row.id,
    name: row.name,
    handle: row.handle,
    avatar: row.avatar,
    planetColors: row.planet_colors,
    createdAt: row.created_at,
    // 행성 아래 한 문장. 모아둔 문장 중에 고른 것이다.
    pinnedQuoteId: row.pinned_quote_id ?? null,
    quote: row.pinned
      ? {
          id: row.pinned.id,
          text: row.pinned.text,
          page: row.pinned.page,
          bookId: row.pinned.book_id,
          bookTitle: row.pinned.books?.title ?? "",
          bookAuthor: row.pinned.books?.author ?? "",
        }
      : null,
    // bookIds 는 "행성에 자리를 잡은 책" 이다. 담아두기만 한 책(want_to_read)은
    // 읽은 책이 아니므로 권수·유사도·궤적·독자 수에서 빠져야 한다.
    bookIds: (row.user_books ?? []).filter((ub) => !ub.want_to_read).map((ub) => ub.book_id),
    /** 담아둔 책. 읽고 싶은 책 탭에서만 쓴다. */
    wantBookIds: (row.user_books ?? []).filter((ub) => ub.want_to_read).map((ub) => ub.book_id),
    manualFriends: (row.friends ?? []).map((f) => f.friend_id),
    /** 북BTI 유형. 아직 안 한 사람은 null 이다. */
    bookType: row.book_type ?? null,
  };
}

const PROFILE_SELECT = `
  id, name, handle, avatar, planet_colors, created_at, book_type,
  pinned_quote_id,
  pinned:quotes!profiles_pinned_quote_id_fkey ( id, text, page, book_id, books ( title, author ) ),
  user_books ( book_id, want_to_read ),
  friends!friends_user_id_fkey ( friend_id )
`;

export async function listUsers() {
  const { data, error } = await supabase.from("profiles").select(PROFILE_SELECT);
  if (error) throw error;
  return data.map(foldProfile);
}

export async function getUser(userId) {
  const { data, error } = await supabase
    .from("profiles")
    .select(PROFILE_SELECT)
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  return data ? foldProfile(data) : null;
}

export async function updateProfile(userId, patch) {
  const row = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.handle !== undefined) row.handle = patch.handle;
  if (patch.avatar !== undefined) row.avatar = patch.avatar;
  if (patch.planetColors !== undefined) row.planet_colors = patch.planetColors;
  /* 북BTI. 다시 해서 유형이 바뀌면 잰 날도 같이 바꾼다. */
  if (patch.bookType !== undefined) { row.book_type = patch.bookType; row.book_type_at = new Date().toISOString(); }
  // 걸어둘 문장은 quotes 쪽 pinQuote 가 맡는다
  const { error } = await supabase.from("profiles").update(row).eq("id", userId);
  if (error) throw error;
}

/**
 * onSnapshot(collection(db,"users")) 대체.
 *
 * Firestore 시절엔 bookIds / manualFriends 가 users 문서 안의 배열이어서
 * 문서 하나만 구독하면 됐다. 지금은 각각 별도 테이블이므로
 * profiles 만 구독하면 "책을 담아도 독자 수가 그대로"인 문제가 생긴다.
 */
export function subscribeUsers(onChange) {
  const channel = supabase
    .channel("profiles-changes")
    .on("postgres_changes", { event: "*", schema: "public", table: "profiles" }, onChange)
    .on("postgres_changes", { event: "*", schema: "public", table: "user_books" }, onChange)
    .on("postgres_changes", { event: "*", schema: "public", table: "friends" }, onChange)
    .subscribe();
  return () => supabase.removeChannel(channel);
}

// ---------- 이웃(친구) ----------

export async function addFriend(userId, friendId) {
  const { error } = await supabase
    .from("friends")
    .upsert({ user_id: userId, friend_id: friendId }, { onConflict: "user_id,friend_id" });
  if (error) throw error;
}

export async function removeFriend(userId, friendId) {
  const { error } = await supabase
    .from("friends")
    .delete()
    .eq("user_id", userId)
    .eq("friend_id", friendId);
  if (error) throw error;
}

// ---------- 위시리스트 ----------

export async function listWishlist(userId) {
  const { data, error } = await supabase
    .from("wishlist")
    .select("book_id, from_user_id, added_at, books ( title, author ), profiles!wishlist_from_user_id_fkey ( name )")
    .eq("user_id", userId);
  if (error) throw error;
  return data.map((w) => ({
    bookId: w.book_id,
    bookTitle: w.books?.title ?? "",
    bookAuthor: w.books?.author ?? "",
    fromUserId: w.from_user_id,
    fromUserName: w.profiles?.name ?? "",
    addedAt: w.added_at,
  }));
}

export async function addToWishlist(userId, bookId, fromUserId) {
  const { error } = await supabase
    .from("wishlist")
    .upsert(
      { user_id: userId, book_id: bookId, from_user_id: fromUserId ?? null },
      { onConflict: "user_id,book_id" }
    );
  if (error) throw error;
}

export async function removeFromWishlist(userId, bookId) {
  const { error } = await supabase
    .from("wishlist")
    .delete()
    .eq("user_id", userId)
    .eq("book_id", bookId);
  if (error) throw error;
}

// ---------- 알림 ----------

export async function listNotifications(userId) {
  const { data, error } = await supabase
    .from("notifications")
    .select("id, type, book_title, read, created_at, from_name, profiles!notifications_from_user_id_fkey ( name )")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data.map((n) => ({
    id: n.id,
    type: n.type,
    // 이전된 알림은 발신자 id 역추적이 실패할 수 있어 원본 이름으로 대체한다
    fromUserName: n.profiles?.name ?? n.from_name ?? "누군가",
    bookTitle: n.book_title ?? undefined,
    read: n.read,
    createdAt: n.created_at,
  }));
}

export async function sendNotification({ toUserId, fromUserId, type, bookTitle = null }) {
  if (toUserId === fromUserId) return;
  const { error } = await supabase
    .from("notifications")
    .insert({ user_id: toUserId, from_user_id: fromUserId, type, book_title: bookTitle });
  if (error) throw error;
}

/**
 * 내 알림을 모두 지운다.
 *
 * RLS 가 auth.uid() = user_id 로 묶여 있어 남의 알림은 지워지지 않는다.
 * 그래도 eq 를 함께 걸어둔다 — 정책이 유일한 방어선이면 정책을 고칠 때
 * 조용히 무너진다.
 */
export async function clearNotifications(userId) {
  const { error } = await supabase.from("notifications").delete().eq("user_id", userId);
  if (error) throw error;
}

export async function markNotificationsRead(userId) {
  const { error } = await supabase
    .from("notifications")
    .update({ read: true })
    .eq("user_id", userId)
    .eq("read", false);
  if (error) throw error;
}

/** 같은 사람이 같은 행성에 다시 들르기까지 이 시간 안이면 새로고침으로 본다 */
const VISIT_GAP_MS = 30 * 60 * 1000;

/**
 * 남의 행성에 들른 것을 남긴다.
 *
 * 새로고침은 세지 않는다. 30분 안에 같은 행성을 다시 열면 건너뛴다 —
 * 뒤로 갔다 다시 들어오는 것까지 방문으로 세면 숫자가 부풀어 뜻이 없어진다.
 *
 * 실패해도 조용히 넘어간다. 방문 기록 때문에 남의 행성이 안 열리면 안 된다.
 */
export async function recordPlanetVisit(ownerId, visitorId) {
  if (!ownerId || !visitorId || ownerId === visitorId) return;

  const key = `muroo-visited-${visitorId}-${ownerId}`;
  try {
    const last = Number(localStorage.getItem(key) || 0);
    if (Date.now() - last < VISIT_GAP_MS) return;
    localStorage.setItem(key, String(Date.now()));
  } catch {
    // 저장이 막힌 브라우저에서는 새로고침을 걸러낼 수 없다.
    // 부풀린 숫자를 남기느니 세지 않는다.
    return;
  }

  try {
    await supabase.from("planet_visits").insert({ owner_id: ownerId, visitor_id: visitorId });
  } catch {
    /* 방문 기록은 있으면 좋은 것이지 필수가 아니다 */
  }
}

/**
 * 내 행성에 다녀간 사람 수.
 *
 * 방문 줄은 전부 쌓이지만 세는 건 사람이다. 한 사람이 다섯 번 온 것을
 * '5명' 이라고 말하면 거짓이 된다.
 *
 * RLS 가 주인 것만 돌려주므로 남의 수를 물어봐도 0 이 나온다.
 */
export async function countPlanetVisitors(ownerId) {
  if (!ownerId) return 0;
  const { data, error } = await supabase
    .from("planet_visits")
    .select("visitor_id")
    .eq("owner_id", ownerId);
  if (error) return 0;
  return new Set((data ?? []).map((r) => r.visitor_id)).size;
}
